import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Combobox } from "@/components/ui";
import { useAccounts } from "@/hooks/use-accounts";
import { useAccount } from "@/hooks/use-account";

const COMBOBOX_LIMIT = 20;

export interface AccountSearchComboboxProps {
  /** Currently-selected account id, or null when no selection. */
  value: string | null;
  /** Notified on every selection change with the chosen id + display label. */
  onChange: (accountId: string | null, label: string) => void;
  /** Optional placeholder override for the text input. */
  placeholder?: string;
  /** Optional wrapper className for layout composition inside form rows. */
  className?: string;
}

/**
 * Search-as-you-type account picker over the main `/accounts` endpoint.
 * The account is the regulatorily-anchored owner of inventory devices per
 * AWWA M14 §10; the installation form binds `account_id` through this
 * picker. Server-side typeahead via the `name_search` query param at
 * limit=20; the `useAccount` detail fetch resolves the selected chip label
 * when the chosen account falls outside the current search page.
 */
export function AccountSearchCombobox({
  value,
  onChange,
  placeholder,
  className = "w-full",
}: AccountSearchComboboxProps) {
  const { t } = useTranslation("device-inventory");
  const [search, setSearch] = useState("");

  const accountsQuery = useAccounts({
    name_search: search || undefined,
    limit: COMBOBOX_LIMIT,
  });
  const detailQuery = useAccount(value);

  const options = useMemo(
    () => (accountsQuery.data?.items ?? []).map((a) => ({ value: a.id, label: a.name })),
    [accountsQuery.data],
  );

  const selectedLabel = useMemo(() => {
    if (!value) return undefined;
    const fromOptions = options.find((o) => o.value === value)?.label;
    if (fromOptions) return fromOptions;
    if (detailQuery.data) return detailQuery.data.name;
    return undefined;
  }, [value, options, detailQuery.data]);

  return (
    <Combobox
      className={className}
      options={options}
      value={value ?? ""}
      onChange={(next) => {
        const label = options.find((o) => o.value === next)?.label ?? selectedLabel ?? "";
        onChange(next || null, label);
      }}
      onSearch={setSearch}
      loading={accountsQuery.isFetching}
      selectedLabel={selectedLabel}
      placeholder={placeholder ?? t("combobox.accountSearchPlaceholder")}
    />
  );
}
